  import React, { useState, useEffect } from 'react';
  import { motion, AnimatePresence } from 'framer-motion';
  import { Link } from 'react-router-dom';
  import { getAuth } from 'firebase/auth';
  import { db } from './firebase';
  import { collection, setDoc, updateDoc, getDoc, doc, getDocs, query, where } from 'firebase/firestore';

  const Leagues = () => {
    const [leagueName, setLeagueName] = useState('');
    const [leagueType, setLeagueType] = useState('match');
    const [startingGameweek, setStartingGameweek] = useState(1);
    const [userLeagues, setUserLeagues] = useState([]);
    const [activeTab, setActiveTab] = useState('view');
    const [feedback, setFeedback] = useState({ type: '', message: '' });
    const [viewLeagueType, setViewLeagueType] = useState('match');
    const [searchTerm, setSearchTerm] = useState('');
    const [leagueCode, setLeagueCode] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
      const searchParams = new URLSearchParams(window.location.search);
      const selectedType = searchParams.get('selectedType');

      if (selectedType === 'table') {
        setViewLeagueType(selectedType);
      }

      fetchUserLeagues();
    }, []);

    const fetchUserLeagues = async () => {
      setIsLoading(true);
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        setIsLoading(false);
        return;
      }

      const matchLeaguesQuery = query(collection(db, 'leagues'), where('members', 'array-contains', user.uid), where('type', '==', 'match'));
      const tableLeaguesQuery = query(collection(db, 'leagues'), where('members', 'array-contains', user.uid), where('type', '==', 'table'));

      try {
        const [matchLeaguesSnapshot, tableLeaguesSnapshot] = await Promise.all([
          getDocs(matchLeaguesQuery),
          getDocs(tableLeaguesQuery)
        ]);

        const matchLeagues = matchLeaguesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), type: 'match' }));
        const tableLeagues = tableLeaguesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), type: 'table' }));

        setUserLeagues([...matchLeagues, ...tableLeagues].sort((a, b) => a.name.localeCompare(b.name)));
      } catch (error) {
        console.error("Error fetching leagues: ", error);
        setFeedback({ type: 'error', message: 'Error fetching leagues. Please try again.' });
      } finally {
        setIsLoading(false);
      }
    };

    const generateLeagueCode = async () => {
      const consonants = 'BCDFGHJKLMNPQRSTVWXYZ';
      const numbers = '123456789';
      const characters = consonants + numbers + consonants.toLowerCase();
      
      while (true) {
        let code = '';
        for (let i = 0; i < 8; i++) {
          code += characters.charAt(Math.floor(Math.random() * characters.length));
        }
        
        const leagueQuery = query(collection(db, 'leagues'), where('leagueCode', '==', code));
        const leagueSnapshot = await getDocs(leagueQuery);
        
        if (leagueSnapshot.empty) {
          return code;
        }
      }
    };

    const handleCreateLeague = async (e) => {
      e.preventDefault();
      const auth = getAuth();
      const user = auth.currentUser;
      if (!leagueName) return;
    
      const leagueCode = await generateLeagueCode();
      const leagueData = {
        name: leagueName,
        type: leagueType,
        startingGameweek: leagueType === 'match' ? startingGameweek : 1,
        members: [user.uid],
        createdBy: user.uid,
        createdAt: new Date(),
        leagueCode: leagueCode,
      };

      const adminCreatedQuery = query(
        collection(db, 'leagues'),
        where('createdBy', '==', user.uid),
        where('type', '==', leagueType)
      );

      const adminCreatedSnapshot = await getDocs(adminCreatedQuery);
      const adminCreatedCount = adminCreatedSnapshot.size;

      if (adminCreatedCount >= 3) {
        setFeedback({ type: 'error', message: 'You can only create 3 leagues of each type. Please delete one before creating a new one.' });
        return;
      }
    
      try {
        const specificDocId = `${leagueCode}_${user.uid}`;
    
        const docRef = doc(db, 'leagues', specificDocId);
        await setDoc(docRef, leagueData);
    
        const userDocRef = doc(db, 'users', user.uid);
        const fieldToUpdate = leagueType === 'match' ? 'in_match_predictor_leagues' : 'in_table_predictor_leagues';
        
        const userDoc = await getDoc(userDocRef);
        const userData = userDoc.data();
    
        const updatedField = userData[fieldToUpdate] ? [...userData[fieldToUpdate], specificDocId] : [specificDocId];
    
        const updatedAdminLeagues = userData.admin_leagues ? [...userData.admin_leagues, specificDocId] : [specificDocId];
    
        const updateObject = {
          [fieldToUpdate]: updatedField,
          admin_leagues: updatedAdminLeagues,
        };
    
        await updateDoc(userDocRef, updateObject);
    
        setLeagueName('');
        setLeagueType('match');
        setStartingGameweek(1);
        fetchUserLeagues();
        setFeedback({ type: 'success', message: 'League created successfully!' });
      } catch (error) {
        console.error("Error creating league: ", error);
        setFeedback({ type: 'error', message: 'Error creating league. Please try again.' });
      }
    };

    const handleJoinLeague = async (e) => {
      e.preventDefault();
      const auth = getAuth();
      const user = auth.currentUser;
      if (!leagueCode) return;

      try {
        const leagueQuery = query(collection(db, 'leagues'), where('leagueCode', '==', leagueCode));
        const leagueSnapshot = await getDocs(leagueQuery);

        if (leagueSnapshot.empty) {
          setFeedback({ type: 'error', message: 'League not found. Please check the code and try again.' });
          return;
        }

        const leagueDoc = leagueSnapshot.docs[0];
        const leagueData = leagueDoc.data();

        if (leagueData.members.includes(user.uid)) {
          setFeedback({ type: 'error', message: 'You are already a member of this league.' });
          return;
        }

        const updatedMembers = [...leagueData.members, user.uid];
        await updateDoc(leagueDoc.ref, { members: updatedMembers });

        const userDocRef = doc(db, 'users', user.uid);
        const fieldToUpdate = leagueData.type === 'match' ? 'in_match_predictor_leagues' : 'in_table_predictor_leagues';
        
        const userDoc = await getDoc(userDocRef);
        const userData = userDoc.data();

        const updatedField = userData[fieldToUpdate] ? [...userData[fieldToUpdate], leagueDoc.id] : [leagueDoc.id];

        await updateDoc(userDocRef, { [fieldToUpdate]: updatedField });

        setLeagueCode('');
        fetchUserLeagues();
        setFeedback({ type: 'success', message: 'Successfully joined the league!' });
      } catch (error) {
        console.error("Error joining league: ", error);
        setFeedback({ type: 'error', message: 'Error joining league. Please try again.' });
      }
    };

    const FeedbackMessage = ({ type, message }) => (
      <motion.p
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className={`text-sm mb-2 text-center ${type === 'success' ? 'text-green-600' : 'text-red-600'}`}
      >
        {message}
      </motion.p>
    );

    const filteredLeagues = userLeagues.filter(league => 
      league.type === viewLeagueType && 
      league.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (isLoading) {
      return (
        <div className="flex justify-center items-center h-screen">
          <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-premier-purple"></div>
        </div>
      );
    }

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="container mx-auto px-4 py-8 max-w-3xl"
      >
        <h1 className="text-4xl font-bold mb-6 text-premier-purple text-center">Leagues</h1>

        <div className="mb-8 bg-white p-6 rounded-lg shadow-md">
          <div className="flex justify-center mb-4">
            <button
              onClick={() => setActiveTab('view')}
              className={`px-4 py-2 rounded-l-lg ${activeTab === 'view' ? 'bg-premier-purple text-white' : 'bg-gray-200 text-gray-700'} transition duration-300`}
            >
              View Leagues
            </button>
            <button
              onClick={() => setActiveTab('create')}
              className={`px-4 py-2 rounded-r-lg ${activeTab === 'create' ? 'bg-premier-purple text-white' : 'bg-gray-200 text-gray-700'} transition duration-300`}
            >
              Create/Join League
            </button>
          </div>

          {activeTab === 'create' && (
          <>
            <AnimatePresence>
              {feedback.message && (
                <FeedbackMessage type={feedback.type} message={feedback.message} />
              )}
            </AnimatePresence>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="space-y-8"
            >
              <div className="flex flex-col md:flex-row md:space-x-8 space-y-8 md:space-y-0">
                <div className="flex-1 bg-gray-50 p-6 rounded-lg shadow-md">
                  <h2 className="text-2xl font-semibold mb-4 text-premier-purple">Create a New League</h2>
                  <form onSubmit={handleCreateLeague} className="space-y-4">
                    <div>
                      <label htmlFor="leagueName" className="block text-sm font-medium text-gray-700">League Name</label>
                      <input
                        type="text"
                        id="leagueName"
                        maxLength="30"
                        value={leagueName}
                        onChange={(e) => setLeagueName(e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-premier-purple focus:ring focus:ring-premier-purple focus:ring-opacity-50"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="leagueType" className="block text-sm font-medium text-gray-700">League Type</label>
                      <select
                        id="leagueType"
                        value={leagueType}
                        onChange={(e) => setLeagueType(e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-premier-purple focus:ring focus:ring-premier-purple focus:ring-opacity-50"
                      >
                        <option value="match">Match Predictor</option>
                        <option value="table">Table Predictor</option>
                      </select>
                    </div>
                    {leagueType === 'match' && (
                      <div>
                        <label htmlFor="startingGameweek" className="block text-sm font-medium text-gray-700">Starting Gameweek</label>
                        <select
                          id="startingGameweek"
                          value={startingGameweek}
                          onChange={(e) => setStartingGameweek(Number(e.target.value))}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-premier-purple focus:ring focus:ring-premier-purple focus:ring-opacity-50"
                        >
                          {[...Array(38)].map((_, i) => (
                            <option key={i} value={i + 1}>Gameweek {i + 1}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <button
                      type="submit"
                      className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-premier-purple hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-premier-purple transition duration-300"
                    >
                      Create League
                    </button>
                  </form>
                </div>

                <div className="flex-1 bg-gray-50 p-6 rounded-lg shadow-md">
                  <h2 className="text-2xl font-semibold mb-4 text-premier-purple">Join a League</h2>
                  <form onSubmit={handleJoinLeague} className="space-y-4">
                    <div>
                      <label htmlFor="leagueCode" className="block text-sm font-medium text-gray-700">League Code</label>
                      <input
                        type="text"
                        id="leagueCode"
                        value={leagueCode}
                        onChange={(e) => setLeagueCode(e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-premier-purple focus:ring focus:ring-premier-purple focus:ring-opacity-50"
                        required
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-premier-purple hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-premier-purple transition duration-300"
                    >
                      Join League
                    </button>
                  </form>
                </div>
              </div>
            </motion.div>
          </>
        )}

          {activeTab === 'view' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-2xl font-semibold mb-4">Your Leagues</h2>
              <div className="mb-4">
                <label htmlFor="leagueTypeFilter" className="block text-sm font-medium text-gray-700">League Type</label>
                <select
                  id="leagueTypeFilter"
                  value={viewLeagueType}
                  onChange={(e) => setViewLeagueType(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-premier-purple focus:ring focus:ring-premier-purple focus:ring-opacity-50"
                >
                  <option value="match">Match Predictor</option>
                  <option value="table">Table Predictor</option>
                </select>
              </div>
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Search leagues..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-premier-purple"
                />
              </div>
              {filteredLeagues.length > 0 ? (
                <div className="space-y-4">
                  {filteredLeagues.map((league) => (
                    <motion.div
                      key={league.id}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                      className="bg-gray-50 rounded-lg shadow-md p-4 hover:shadow-lg transition duration-300"
                    >
                      <h3 className="text-xl font-semibold text-premier-purple">{league.name}</h3>
                      <p className="text-gray-600 mb-2 text-sm">Code: <span className='font-bold'>{league.leagueCode}</span></p>
                      <p className="text-gray-600">Type: {league.type === 'match' ? 'Match Predictor' : 'Table Predictor'}</p>
                      {league.type === 'match' && (
                        <p className="text-gray-600">Starting Gameweek: {league.startingGameweek}</p>
                      )}
                      <p className="text-gray-600">Current Members: {league.members.length}</p>
                      <Link
                        to={`/leaderboard/${league.type}-predictor?leagueCode=${league.leagueCode}`}
                        className="mt-2 inline-block px-4 py-2 bg-premier-purple text-white rounded hover:bg-purple-700 transition duration-300"
                      >
                        View League
                      </Link>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600">No leagues found.</p>
              )}
            </motion.div>
          )}
        </div>
      </motion.div>
    );
  };

  export default Leagues;